import { createSelector } from '@reduxjs/toolkit';

import { MessageType } from '@wix/ambassador-innovation-widget-v1-message/types';

import { RootState } from '@/store';
import {
  senderIsAnswer,
  senderIsAssistant,
  senderIsVisitor,
} from '@/utils/sender';

import { MESSAGES_MAX_FOR_INTRO, MESSAGES_PAGE_SIZE } from './config';
import { messagesAdapter } from './messagesSlice';
import { isSpecialMessage } from './utils';
import { SELECT_OPTION_ACTION_TYPE } from './messagesThunks';

export const value = (state: RootState) => state.messages;
const adapterSelectors = messagesAdapter.getSelectors<RootState>(value);

export const entities = adapterSelectors.selectEntities;
export const ids = adapterSelectors.selectIds;
export const total = adapterSelectors.selectTotal;

const _list = adapterSelectors.selectAll;
export const formSubmissions = createSelector(_list, (l) =>
  l.reduce((acc, m) => {
    if (m.messageType === MessageType.CONTACT_FORM_SUBMITTED && m.answerTo) {
      acc.add(m.answerTo);
    }
    return acc;
  }, new Set<string>()),
);

export const historyContactFormIds = createSelector(
  value,
  (v) => new Set(v.historyContactFormIds ?? []),
);

export const activeContactFormId = createSelector(
  formSubmissions,
  _list,
  (submittedFormIds, messages) =>
    messages.findLast(
      (message) =>
        message.messageType === MessageType.CONTACT_FORM &&
        !submittedFormIds.has(message.id),
    )?.id,
);

export const hasContactForm = createSelector(_list, (messages) =>
  messages.some((message) => message.messageType === MessageType.CONTACT_FORM),
);

// Not scoped to the current conversationId: a merge means the visitor's
// history is one continuous thread going forward, so a CTA that's already
// loaded and visible (even if added under a previous conversationId, before
// a merge) genuinely is "already sent" — scrolling to it is correct, not a
// stale/duplicate result.
export const whatsAppSocialButtonMessageId = createSelector(
  _list,
  (messages) =>
    messages.find(
      (message) => message.messageType === MessageType.WHATSAPP_SOCIAL_BUTTON,
    )?.id,
);

export const hasWhatsAppSocialButtonMessage = createSelector(
  whatsAppSocialButtonMessageId,
  (id) => Boolean(id),
);

export const pendingScrollToMessageId = createSelector(
  value,
  (v) => v.pendingScrollToMessageId,
);

export const isSendingWhatsAppSocialButtonMessage = createSelector(
  value,
  (v) => v.isSendingWhatsAppSocialButtonMessage,
);

export const visibleMessages = createSelector(
  formSubmissions,
  activeContactFormId,
  _list,
  (submittedFormIds, activeFormId, messages) =>
    messages.filter((message) => {
      if (submittedFormIds.has(message.id)) {
        return false;
      }

      if (message.messageType !== MessageType.CONTACT_FORM) {
        return true;
      }

      return message.id === activeFormId;
    }),
);

export const request = createSelector(value, (v) => v.request);
export const isTyping = createSelector(value, (v) => v.isTyping);
export const hasUnread = createSelector(value, (v) => v.hasUnread);
export const firstUnreadMessageId = createSelector(
  value,
  (v) => v.firstUnreadMessageId,
);
export const pagingMetadata = createSelector(value, (v) => v.pagingMetadata);
export const conversationId = createSelector(value, (v) => v.conversationId);
export const realtimeRenewAttempt = createSelector(
  value,
  (v) => v.realtimeRenewAttempt,
);
// Bumped by resetConversation — unlike conversationId, this changes even if
// a merge/reset returns to the same conversation id, so it's the correct
// token for "is this async operation's result still current" checks (a
// plain conversationId match can't distinguish two different sends/loads
// for the same id across a reset in between).
export const conversationGeneration = createSelector(
  value,
  (v) => v.conversationGeneration,
);
export const pendingMessages = createSelector(value, (v) => v.pendingMessages);

export const isIdle = createSelector(request, (r) => r === 'idle');
export const isLoading = createSelector(request, (r) => r === 'loading');
export const isLoadingHistory = createSelector(
  request,
  (r) => r === 'loadingHistory',
);

export const isRequestingConversationId = createSelector(
  value,
  (v) => v.conversationIdRequest === 'loading',
);

export const shouldLoadConversationId = createSelector(
  conversationId,
  isRequestingConversationId,
  (conversation, requesting) => !conversation && !requesting,
);

export const isError = createSelector(request, (r) => r === 'error');

export const lastAnswer = createSelector(_list, (l) => {
  const last = l.at(-1);
  return senderIsAnswer(last?.sender) ? last : undefined;
});

export const lastUnread = createSelector(
  hasUnread,
  lastAnswer,
  (unread, answer) => (unread ? answer : undefined),
);

export const hasOngoingConversation = createSelector(_list, (list) => {
  const last = list.findLast(
    (m) => senderIsAssistant(m.sender) && !isSpecialMessage(m),
  );
  if (!last || !last.createdDate) {
    return false;
  }
  return Date.now() - last.createdDate < 1000 * 60 * 10; // 10 minutes
});

export const shouldLoadMessages = createSelector(
  pagingMetadata,
  isIdle,
  (metadata, idle) => metadata === null && idle,
);

export const isLoaded = createSelector(
  pagingMetadata,
  (metadata) => metadata !== null,
);

export const loadingFirstPage = createSelector(
  isLoaded,
  isLoading,
  (loaded, loading) => !loaded && loading,
);

export const shouldSendIntro = createSelector(
  isLoaded,
  _list,
  total,
  (loaded, l, t) =>
    loaded &&
    t <= MESSAGES_MAX_FOR_INTRO &&
    !l.find((i) => i.messageType === MessageType.INTRO),
);
export const shouldSendLegal = createSelector(
  isLoaded,
  _list,
  total,
  (loaded, l, t) =>
    loaded &&
    t <= MESSAGES_MAX_FOR_INTRO &&
    !l.find((i) => i.messageType === MessageType.LEGAL),
);

export const shouldSendOfflineMessage = createSelector(
  isLoaded,
  _list,
  total,
  (loaded, l, t) =>
    loaded &&
    t <= MESSAGES_MAX_FOR_INTRO &&
    !l.find((i) => i.messageType === MessageType.OFFLINE),
);

export const shouldSendSuggestions = createSelector(
  isLoaded,
  _list,
  total,
  (loaded, l, t) =>
    loaded &&
    t <= MESSAGES_MAX_FOR_INTRO &&
    !l.find((i) => i.messageType === MessageType.SUGGESTED_QUESTION),
);

export const shouldSendFaqButtons = createSelector(
  isLoaded,
  _list,
  total,
  (loaded, l, t) =>
    loaded &&
    t <= MESSAGES_MAX_FOR_INTRO &&
    !l.find((i) => i.messageType === MessageType.FAQ_QUESTION),
);

export const noContactFormOnLastPage = createSelector(
  isLoaded,
  visibleMessages,
  historyContactFormIds,
  (loaded, l, historyFormIds) =>
    loaded &&
    !l
      .slice(-MESSAGES_PAGE_SIZE)
      .find(
        (i) =>
          i.messageType === MessageType.CONTACT_FORM &&
          !historyFormIds.has(i.id),
      ),
);

export const newestVisitorOptionsFieldsMessageId = createSelector(
  _list,
  (list) => list.findLast((m) => Boolean(m.visitorOptions?.length))?.id,
);

export const hasAnyVisitorMessages = createSelector(
  isLoaded,
  _list,
  (loaded, list) => loaded && list.some((m) => senderIsVisitor(m.sender)),
);

export const makeRepliesSelector = () =>
  createSelector(
    [_list, (_, messageId: string) => messageId],
    (list, messageId) => list.filter((m) => m.answerTo === messageId),
  );

export const makeSelectedOptionSelector = () =>
  createSelector([makeRepliesSelector()], (replies) =>
    replies?.reduce((acc, reply) => {
      if (acc || reply.messageType !== MessageType.VISITOR_OPTION_SELECTED) {
        return acc;
      }
      return (
        reply.body?.additionalData?.find(
          (data) => data.type === SELECT_OPTION_ACTION_TYPE,
        )?.action || acc
      );
    }, ''),
  );
